import { useState } from "react";
import { ButtonAsButton } from "@made-people/blueprint-button";
import { ResponsiveImage } from "@made-people/blueprint-responsive-image";
import { useTranslate } from "@made-people/grease-translations";
import type { Dispatch, SetStateAction } from "react";
import { useFrontendContext } from "../../../../hooks/use-frontend-context";
import { useMmBackendClient } from "../../../../hooks/use-mm-backend-client";
import type {
  ReturnAddressTable,
  ReturnFlowState,
  ReturnReasonsTable,
  ReturnSelections,
  ReturnsLookupResponse,
} from "./non-inretrn-return-flow";
import {
  buildReturnCards,
  formatPrice,
  getTotalRefund,
  parseReturnReasons,
} from "./return-items-selection";
import { ShowSummaryConfirmationModal } from "./show-summary-confirmation-modal";

type ReturnOrderResponse = {
  status: string;
  returnCode: string;
  message: string;
};

const ROW_COUNTRY_CODE = "ROW";

export const getReturnAddress = (
  table: ReturnAddressTable,
  countryCode: string,
): string => {
  const headers = table.thead.map((cell) => cell.value);
  const codeIndex = headers.indexOf("Country_Code");
  const addressIndex = headers.indexOf("Address");

  const findRow = (code: string) =>
    table.tbody.find((row) => row.body[codeIndex]?.value === code);

  const row = findRow(countryCode) ?? findRow(ROW_COUNTRY_CODE);

  return row?.body[addressIndex]?.value ?? "";
};

type ShowSummaryProps = {
  data: ReturnsLookupResponse;
  email: string;
  dayLimit: number;
  returnAddress: ReturnAddressTable;
  modalHeader?: string;
  modalSubHeader?: string;
  returnReasons?: ReturnReasonsTable;
  selections: ReturnSelections;
  setState: Dispatch<SetStateAction<ReturnFlowState>>;
};

export const ShowSummary = ({
  data,
  email,
  dayLimit,
  returnAddress,
  modalHeader,
  modalSubHeader,
  returnReasons,
  selections,
  setState,
}: ShowSummaryProps) => {
  const translate = useTranslate();
  const frontendContext = useFrontendContext();
  const mmBackendClient = useMmBackendClient();
  const parsedReturnReasons = parseReturnReasons(returnReasons);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const selectedCards = buildReturnCards(data).filter(
    (card) => !card.isReturned && selections[card.key],
  );
  const totalRefund = getTotalRefund(selectedCards);

  const handleConfirmReturn = async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    const payload = {
      orderId: data.orderNumber,
      email,
      dayLimit,
      returnAddress: getReturnAddress(returnAddress, data.deliveryCountry),
      items: selectedCards.map((card) => ({
        lineId: card.item.orderLineId,
        sku: card.item.sku + card.item.variantSku,
        productName: card.item.name,
        localSize: card.item.localSize,
        mediaUrl: card.item.imageUrl,
        returnReason:
          parsedReturnReasons.find(
            (reason) => reason.code === selections[card.key],
          )?.label ?? selections[card.key],
        returnReasonCode: selections[card.key],
        quantity: 1,
      })),
    };

    try {
      const response = await mmBackendClient.post<ReturnOrderResponse>(
        "/return/return-order",
        payload,
        {
          headers: { "Content-Type": "application/json" },
        },
      );

      if (response.data.status === "success") {
        setState({
          status: "successState",
          returnCode: response.data.returnCode,
          deliveryCountry: data.deliveryCountry,
        });
      } else {
        setState({ status: "errorState" });
      }
    } catch (error) {
      console.error(error);
      setState({ status: "errorState" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-component-spacing-3xl mb-component-spacing-5xl px-component-spacing-sm">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm font-medium text-gray-900">
            {translate("Account_OrderDetails_OrderNumber_Label")}:{" "}
            {data.orderNumber}
          </span>
          <span className="text-sm font-medium text-gray-700">
            {translate("Return_Items_TotalToReturn", {
              count: selectedCards.length,
            })}
          </span>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {selectedCards.map((card) => {
            const reasonLabel =
              parsedReturnReasons.find(
                (reason) => reason.code === selections[card.key],
              )?.label ?? selections[card.key];

            return (
              <div key={card.key} className="flex flex-col rounded-md p-2">
                <div className="relative w-full overflow-hidden rounded-[5px] bg-gray-100 aspect-product-image">
                  <span className="absolute left-0 top-[10px] z-10 flex items-center bg-black px-4 py-1 text-sm text-white rounded-r-[3px]">
                    {translate("return_flow_selected_item_badge")}
                  </span>
                  {card.item.imageUrl && (
                    <ResponsiveImage
                      image={{ src: card.item.imageUrl, alt: card.item.name }}
                      loading="lazy"
                      displaySize="240px"
                      className="h-full w-full object-cover"
                    />
                  )}
                  <span className="absolute inset-0 bg-black/30" />
                </div>

                <div className="mt-3 flex items-baseline justify-between gap-2">
                  <span className="text-lg font-bold font-primary text-gray-700">
                    {formatPrice(
                      card.item.unitPrice,
                      data.currencyCode,
                      frontendContext.language,
                    )}
                  </span>
                  <span className="text-xs text-gray-500">
                    {card.item.sku + card.item.variantSku}
                  </span>
                </div>

                <p
                  className="mt-1 truncate text-sm text-gray-800"
                  title={card.item.name}
                >
                  {card.item.name}
                </p>
                <p className="text-sm text-gray-500">
                  {card.item.variant} – {card.item.localSize}
                </p>

                <p className="mt-3 rounded-md bg-gray-100 px-4 py-3 text-sm text-gray-800">
                  {reasonLabel}
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-16 border-t border-gray-200 pt-6">
          <div className="flex items-start justify-between gap-4 px-1">
            <div>
              <p className="text-base font-bold font-primary text-gray-900">
                {translate("return_flow_summary_total_refund")}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                {translate("return_flow_summary_total_refund_disclaimer")}
              </p>
            </div>
            <p className="text-base font-bold font-primary text-gray-900">
              {formatPrice(
                totalRefund,
                data.currencyCode,
                frontendContext.language,
              )}
            </p>
          </div>
        </div>

        <div className="mt-16 flex justify-center gap-8">
          <div className="w-full max-w-xs">
            <ButtonAsButton
              label={translate("return_flow_summary_back_button")}
              type="button"
              disabled={isSubmitting}
              onClick={() =>
                setState({
                  status: "itemSelectionState",
                  data,
                  email,
                  selections,
                })
              }
              variants={{ width: "full", rounded: "md", color: "white" }}
              className="!bg-sand-200 hover:!bg-sand-300 active:!bg-sand-200 !text-black"
            />
          </div>
          <div className="w-full max-w-[500px]">
            <ButtonAsButton
              label={translate("return_flow_confirm_return_button")}
              type="button"
              disabled={isSubmitting}
              progress={isSubmitting ? "loading" : "default"}
              onClick={() => setIsConfirmationOpen(true)}
              variants={{ width: "full", rounded: "md", color: "black" }}
            />
          </div>
        </div>
      </div>

      <ShowSummaryConfirmationModal
        isOpen={isConfirmationOpen}
        header={modalHeader}
        subHeader={modalSubHeader}
        isSubmitting={isSubmitting}
        onConfirm={() => void handleConfirmReturn()}
        onCancel={() => setIsConfirmationOpen(false)}
      />
    </div>
  );
};
